import {
  ChangeDetectionStrategy,
  Component,
  inject,
  Inject,
  Injector,
  LOCALE_ID,
  NgZone,
  OnDestroy,
  OnInit,
  TemplateRef,
  ViewChild,
} from '@angular/core';
import { BehaviorSubject, combineLatest, firstValueFrom, Observable, Subscription, switchMap } from 'rxjs';
import { debounceTime, distinctUntilChanged, filter, map, shareReplay, withLatestFrom } from 'rxjs/operators';
import {
  MonitoringService,
  OrderformAmountItem,
  OrderformOrderState,
  OrderformPaymentProvider,
  OrderformState,
  OrderformSettingsState,
  CustomFormData,
  SiteOwners,
  InterfacePost,
  OrderformTrackingCollectorService,
} from '@orderform/widgets';
import { CommonModule, DOCUMENT } from '@angular/common';
import { ExternalResourcesLoaderService } from '../../services/external-resources-loader.service';
import { OrderformFascade } from '../../fascades/orderform.fascade';
import { HttpClient } from '@angular/common/http';
import {
  ClickResolveDetails,
  Stripe,
  StripeElement,
  StripeElements,
  StripeExpressCheckoutElementClickEvent,
  StripeExpressCheckoutElementConfirmEvent,
} from '@stripe/stripe-js';
import { Store } from '@ngrx/store';
import * as OrderformActions from '../../ngrx/orderform.actions';
import { OrderformService } from '../../services/orderform.service';
import { MatDialog, MatDialogClose } from '@angular/material/dialog';
import { FormUtilitiesModule } from '../../ds24-ui/form-utilities';
import { InputsGeneratorModule } from '../../components/inputs-generator/inputs-generator.module';
import { IconColor, DsSpinnerModule, ModalModule, IconModule, IconName } from '@ds24/elements';
import { InputsGeneratorComponent } from '../../components/inputs-generator/inputs-generator.component';
import { BuySummaryComponent } from '../buy-button-wrapper/buy-summary/buy-summary.component';
import { isPageBuilderElementVisible } from '../../misc/pagebuilder';
import { OrderformIframeService } from '../../services/orderform-iframe.service';

interface ShippingAddress {
  data: CustomFormData;
  matches(billingAddress: BillingAddress): boolean;
}

interface BillingAddress {
  data: CustomFormData;
  matches(shippingAddress: ShippingAddress): boolean;
}

@Component({
  selector: 'orderform-payment-express-checkout',
  templateUrl: './payment-express-checkout.component.html',
  styleUrls: ['./payment-express-checkout.component.scss'],
  standalone: true,
  imports: [
    CommonModule,
    MatDialogClose,
    FormUtilitiesModule,
    InputsGeneratorModule,
    ModalModule,
    DsSpinnerModule,
    BuySummaryComponent,
    IconModule,
  ],
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class PaymentExpressCheckoutComponent implements OnInit, OnDestroy {
  config?: OrderformPaymentProvider;
  private document = this._injector.get(DOCUMENT);
  private expressCheckoutElement: StripeElement;
  private waitForStripeInitialization: Promise<boolean> = null;
  private stripeInstance: Partial<Stripe>;
  private stripeElements: StripeElements;
  uniqueInstanceId: string;
  orderFormStateSubscription: Subscription;
  validationAmount = new BehaviorSubject<{ amount: number; currency: string }>({ amount: 0, currency: '' });
  protected readonly IconColor = IconColor;
  orderStateUpdating$ = this.orderformFascade.pending$;
  totalOrderAmount$ = this.orderformFascade.order$.pipe(map((order) => this.getTotalAmount(order)));
  protected readonly IconName = IconName;

  @ViewChild('dialogEnterCountryAndZipCode', { static: true })
  dialogEnterCountryAndZipCode: TemplateRef<any>;

  @ViewChild('countryAndZipcodeInput', { static: false })
  countryAndZipcodeInput: InputsGeneratorComponent;

  @ViewChild('dialogUpdatedTaxApproval', { static: true })
  dialogUpdatedTaxApproval: TemplateRef<any>;

  @ViewChild('dialogOrderSummary', { static: true })
  dialogOrderSummary: TemplateRef<any>;

  public taxAddressData$ = new BehaviorSubject<CustomFormData>(null);
  public orderStateShippingAddress$ = this.orderformFascade.order$.pipe(map((order) => order.order.shipping_address));
  private taxAddressDataSubscription: Subscription;

  public paymentStatus$ = this.orderformFascade.expressCheckout$.pipe(map((config) => config.status));

  public fieldsConfig$ = this.orderformFascade.settings$.pipe(
    map((settings) => {
      let zipCodeField = null;
      let countryField = null;

      settings.contact_form.fields_shipping_address.forEach((field) => {
        const isCountryField =
          field.postname === 'buyer_address_private_country' || field.postname === 'buyer_address_country';
        if (isCountryField) {
          if (!countryField || (countryField && field.postname === 'buyer_address_private_country')) {
            countryField = {
              ...field,
              in_row_with_next: true,
            };
          }
        }

        const isZipcodeField =
          field.postname === 'buyer_address_zipcode' || field.postname === 'buyer_address_private_zipcode';
        if (isZipcodeField && Object.values(field.depends_on ?? {}).length === 0) {
          if (!zipCodeField || (zipCodeField && field.postname === 'buyer_address_private_zipcode')) {
            zipCodeField = {
              ...field,
              in_row_with_next: true,
            };
          }
        }
      });

      return [countryField, zipCodeField].filter((field) => field !== null);
    })
  );

  isExpressCheckoutInitiable$ = this.orderformFascade.order$.pipe(
    withLatestFrom(this.fieldsConfig$, this.orderformFascade.pending$),
    switchMap(async ([order, fieldsConfig, pending]) => {
      const formIsValid = !this.countryAndZipcodeInput || this.countryAndZipcodeInput.isValid();
      const fieldsFilled = fieldsConfig.every((field) => order.order.shipping_address[field.postname]);

      return formIsValid && fieldsFilled && !pending;
    })
  );

  protected monitoring = this._injector.get(MonitoringService);

  #tracking = inject(OrderformTrackingCollectorService);

  /**
   * place to define properties for the zipcode/country mismatch dialog
   */

  public isGift$ = this.orderformFascade.order$.pipe(
    map((order) => {
      return !!order.order.custom_forms.is_gift;
    })
  );

  isCustomTextOnPayButtonAllowed$: Observable<boolean> = this.orderformFascade.config$.pipe(
    map((data) => data.is_pay_button_label_changed_allowed)
  );

  selectedPaymentMethod$ = this.orderformFascade.order$.pipe(
    map((e) => {
      return e.order.payment_provider;
    }),
    shareReplay(1)
  );

  buttonText$ = this.selectedPaymentMethod$.pipe(
    map(
      (selectedPaymentProv) =>
        selectedPaymentProv.label_data?.default_submit_button?.replace('|', '<br/>') ||
        $localize`:@@orderform.buy_now:Jetzt kaufen`
    )
  );

  paymentLegalHint$ = combineLatest([this.buttonText$, this.orderformFascade.paymentLegalHint$]).pipe(
    map(([buttonText, hint]) => {
      if (hint) {
        hint = hint.replace('__BUTTON__', buttonText);

        while (hint.search(/<a\s*href/g) > 0) {
          hint = hint.replace(/<a\s*href/g, '<a target="_blank" href');
        }
      }

      return hint ?? null;
    })
  );

  constructor(
    protected _injector: Injector,
    public externalResourcesLoader: ExternalResourcesLoaderService,
    private orderformFascade: OrderformFascade,
    @Inject(LOCALE_ID) private locale: string,
    private http: HttpClient,
    private _store: Store<OrderformState>,
    private orderformService: OrderformService,
    private dialog: MatDialog,
    private _ngZone: NgZone,
    private _iframeService: OrderformIframeService
  ) {
    this.taxAddressDataSubscription = this.taxAddressData$.subscribe(
      this.updateShippingAddressFromTaxCalculationDialog.bind(this)
    );
  }

  async ngOnInit() {
    this.uniqueInstanceId = String(new Date().getTime());
    this.config = await firstValueFrom(this.orderformFascade.expressCheckout$);

    this.loadStripe().then(() => {
      this.initializeExpressCheckout();
    });
  }

  protected loadStripe() {
    if (this.stripeInstance) {
      return Promise.resolve(true);
    } else {
      if (null === this.waitForStripeInitialization) {
        this.waitForStripeInitialization = (async () => {
          if (!(<any>window).Stripe) {
            await this.externalResourcesLoader.load(this.document, [this.config.config_data.apiUrl], [], true);
          }

          if (!(<any>window).Stripe) {
            throw new Error('failed to initialize Stripe');
          }

          this.stripeInstance = (<any>window).Stripe(this.config.config_data.publicKey, {
            locale: this.locale.replace(/-[a-zA-Z0-9]{2,3}$/, ''),
          });

          return true;
        })();
      }

      return this.waitForStripeInitialization;
    }
  }

  private async initializeExpressCheckout() {
    const order = await firstValueFrom(this.orderformFascade.order$.pipe(map((order) => order)));

    let amount = this.getTotalAmount(order);

    this.stripeElements = this.stripeInstance.elements({
      mode: 'payment',
      amount: Math.round(amount * 100),
      currency: order.order.currency_code.toLowerCase(),
      setup_future_usage: 'off_session',
      capture_method: 'manual',
    });

    this.orderFormStateSubscription = this.orderformFascade.order$
      .pipe(
        distinctUntilChanged(
          (prev, next) =>
            prev.order_summary.order_total.first.gross === next.order_summary.order_total.first.gross &&
            prev.order_summary.order_total.oth.gross === next.order_summary.order_total.oth.gross
        )
      )
      .subscribe((o) => {
        let amount = o.order_summary.order_total.first.gross;
        if (!amount) {
          amount = o.order_summary.order_total.oth.gross;
          this.validationAmount.next({ amount, currency: o.order.currency_code });
        } else {
          this.validationAmount.next({ amount: 0, currency: o.order.currency_code });
        }
        this.stripeElements.update({ amount: Math.round(amount * 100) });
      });

    this.expressCheckoutElement = this.stripeElements.create('expressCheckout', {
      paymentMethods: { googlePay: 'always', applePay: 'always', amazonPay: 'never', link: 'never', paypal: 'never' },
    });

    this.expressCheckoutElement.on('click', this.onExpressElementClick.bind(this));
    this.expressCheckoutElement.on('confirm', this.onExpressElementConfirm.bind(this));

    this.expressCheckoutElement.mount('#stripe-express-checkout-' + this.uniqueInstanceId);
  }

  private async onExpressElementConfirm(event: StripeExpressCheckoutElementConfirmEvent) {
    let order = await firstValueFrom(this.orderformFascade.order$);

    let shippingAddress = this.getShippingAddress(event, order);
    let billingAddress = this.getBillingAddress(event, order);
    const orderState = {
      shipping_address: shippingAddress?.data ?? this.getBillingAddress(event, order, true).data,
    };

    if (shippingAddress && !shippingAddress.matches(billingAddress)) {
      orderState['billing_address'] = billingAddress.data;
      orderState['has_billing_address'] = 1;
    }

    const newAddress = orderState.shipping_address;
    const oldAddress = order.order.shipping_address;

    this.orderformFascade.setOrderStore(orderState);
    await firstValueFrom(this.orderformFascade.update$());

    order = await firstValueFrom(this.orderformFascade.order$);
    const response = await this.createStripeIntent(order, event.billingDetails.email);

    this.stripeElements.update({
      mode: response.type === 'payment_intent' ? 'payment' : 'setup',
    });

    const returnUrl = this.getReturnUrl();

    if (response.type === 'setup_intent') {
      this.stripeInstance
        .confirmSetup({
          elements: this.stripeElements,
          clientSecret: response.intentClientSecret as string,
          confirmParams: {
            return_url: returnUrl.href,
          },
          redirect: 'if_required',
        })
        .then((result) => {
          if (result.error) {
            throw result.error;
          }
          return this.onIntentConfirmed(result.setupIntent.id, orderState, newAddress, oldAddress);
        });
    } else if (response.type === 'payment_intent') {
      this.stripeInstance
        .confirmPayment({
          elements: this.stripeElements,
          clientSecret: response.intentClientSecret as string,
          confirmParams: {
            return_url: returnUrl.href,
          },
          redirect: 'if_required',
        })
        .then((result) => {
          if (result.error) {
            throw result.error;
          }
          return this.onIntentConfirmed(result.paymentIntent.id, orderState, newAddress, oldAddress);
        });
    } else {
      const errorMessage = 'response type must be one of (setup_intent, payment_intent). Got ' + response.type;
      console.error(errorMessage);
      this.monitoring.trackValidationError(
        'exception:' + errorMessage,
        'frontend',
        'ds-orderform-payment-express-checkout'
      );
      event.paymentFailed({ reason: 'fail' });
    }
  }

  private async updateShippingAddressFromTaxCalculationDialog(data) {
    if (!data) {
      return;
    }

    const order = await firstValueFrom(this.orderformFascade.order$);
    const fieldsConfig = await firstValueFrom(this.fieldsConfig$);
    const newShippingAddress = {
      ...order.order.shipping_address,
    };

    fieldsConfig.forEach((field) => {
      newShippingAddress[field.postname] = data[field.postname] ?? null;
    });

    const privateZipcodeChanged =
      data.buyer_address_private_zipcode &&
      data.buyer_address_private_zipcode !== order.order.shipping_address.buyer_address_private_zipcode;
    const zipcodeChanged =
      data.buyer_address_zipcode && data.buyer_address_zipcode !== order.order.shipping_address.buyer_address_zipcode;

    const privateCountryChanged =
      data.buyer_address_private_country &&
      data.buyer_address_private_country !== order.order.shipping_address.buyer_address_private_country;
    const countryChanged =
      data.buyer_address_country && data.buyer_address_country !== order.order.shipping_address.buyer_address_country;
    if ((privateCountryChanged || countryChanged) && !(privateZipcodeChanged || zipcodeChanged)) {
      if (newShippingAddress.buyer_address_zipcode) {
        newShippingAddress.buyer_address_zipcode = null;
      }
      if (newShippingAddress.buyer_address_private_zipcode) {
        newShippingAddress.buyer_address_private_zipcode = null;
      }
    }

    this.orderformFascade.setOrderStore({
      shipping_address: newShippingAddress,
    });
  }

  private getBillingAddress(
    event: StripeExpressCheckoutElementConfirmEvent,
    order: OrderformOrderState,
    asShipping = false
  ): BillingAddress {
    const billingAddress = event.billingDetails?.address;
    if (!billingAddress) {
      return null;
    }

    const [billingFirstName, ...billingLastName] = event.billingDetails.name.split(' ');

    const address = {
      ...order.order.billing_address,
    };

    address[this.appendBillingIfNeeded('buyer_address_salutation', asShipping)] = '';
    address[this.appendBillingIfNeeded('buyer_address_first_name', asShipping)] = billingFirstName;
    address[this.appendBillingIfNeeded('buyer_address_last_name', asShipping)] =
      billingLastName.length > 0 ? billingLastName.join(' ') : '-';
    address[this.appendBillingIfNeeded('buyer_address_street', asShipping)] =
      billingAddress?.line1 ?? order.order.billing_address.buyer_address_street ?? '';
    address[this.appendBillingIfNeeded('buyer_address_street2', asShipping)] =
      billingAddress?.line2 ?? order.order.billing_address.buyer_address_street2 ?? '';
    address[this.appendBillingIfNeeded('buyer_address_street_number', asShipping)] = '';
    address[this.appendBillingIfNeeded('buyer_address_zipcode', asShipping)] =
      billingAddress?.postal_code ?? order.order.billing_address.buyer_address_zipcode ?? '';
    address[this.appendBillingIfNeeded('buyer_address_city', asShipping)] =
      billingAddress?.city ?? order.order.billing_address.buyer_address_city ?? '';
    address[this.appendBillingIfNeeded('buyer_address_state', asShipping)] =
      billingAddress?.state ?? order.order.billing_address.buyer_address_state ?? '';
    address[this.appendBillingIfNeeded('buyer_address_country', asShipping)] =
      billingAddress?.country ?? order.order.billing_address.buyer_address_country ?? '';

    if (asShipping) {
      address['buyer_address_phone_no'] =
        event.billingDetails.phone ?? order.order.billing_address.buyer_address_phone_no ?? '';
      address['buyer_address_email'] = event.billingDetails.email;
    }

    return {
      data: this.fixAddressForPrivateFields(address, order, asShipping),
      matches(shippingAddress: ShippingAddress): boolean {
        return (
          (this.data.buyer_address_street ?? this.data.buyer_address_street_billing) ===
            shippingAddress.data.buyer_address_street &&
          (this.data.buyer_address_street2 ?? this.data.buyer_address_street2_billing) ===
            shippingAddress.data.buyer_address_street2 &&
          (this.data.buyer_address_zipcode ?? this.data.buyer_address_zipcode_billing) ===
            shippingAddress.data.buyer_address_zipcode &&
          (this.data.buyer_address_city ?? this.data.buyer_address_city_billing) ===
            shippingAddress.data.buyer_address_city &&
          (this.data.buyer_address_state ?? this.data.buyer_address_state_billing) ===
            shippingAddress.data.buyer_address_state &&
          (this.data.buyer_address_country ?? this.data.buyer_address_country_billing) ===
            shippingAddress.data.buyer_address_country
        );
      },
    };
  }

  private getShippingAddress(
    event: StripeExpressCheckoutElementConfirmEvent,
    order: OrderformOrderState
  ): ShippingAddress {
    const shippingAddress = event.shippingAddress?.address;

    if (!shippingAddress) {
      return null;
    }

    const [shippingFirstName, ...shippingLastName] = event.shippingAddress?.name.split(' ') ?? [];

    const address = {
      ...order.order.shipping_address,
      buyer_address_salutation: '',
      buyer_address_email: event.billingDetails.email,
      buyer_address_phone_no: event.billingDetails.phone ?? order.order.shipping_address.buyer_address_phone_no ?? '',
      buyer_address_first_name: shippingFirstName,
      buyer_address_last_name: shippingLastName.length > 0 ? shippingLastName.join(' ') : '-',
      buyer_address_street: shippingAddress?.line1 ?? order.order.shipping_address.buyer_address_street ?? '',
      buyer_address_street2: shippingAddress?.line2 ?? order.order.shipping_address.buyer_address_street2 ?? '',
      buyer_address_street_number: '',
      buyer_address_zipcode: shippingAddress?.postal_code ?? order.order.shipping_address.buyer_address_zipcode ?? '',
      buyer_address_city: shippingAddress?.city ?? order.order.shipping_address.buyer_address_city ?? '',
      buyer_address_state: shippingAddress?.state ?? order.order.shipping_address.buyer_address_state ?? '',
      buyer_address_country: shippingAddress?.country ?? order.order.shipping_address.buyer_address_country ?? '',
    };

    return {
      data: this.fixAddressForPrivateFields(address, order, true),
      matches(billingAddress: BillingAddress): boolean {
        return (
          this.data.buyer_address_street ===
            (billingAddress.data.buyer_address_street ?? billingAddress.data.buyer_address_street_billing) &&
          this.data.buyer_address_street2 ===
            (billingAddress.data.buyer_address_street2 ?? billingAddress.data.buyer_address_street2_billing) &&
          this.data.buyer_address_zipcode ===
            (billingAddress.data.buyer_address_zipcode ?? billingAddress.data.buyer_address_zipcode_billing) &&
          this.data.buyer_address_city ===
            (billingAddress.data.buyer_address_city ?? billingAddress.data.buyer_address_city_billing) &&
          this.data.buyer_address_state ===
            (billingAddress.data.buyer_address_state ?? billingAddress.data.buyer_address_state_billing) &&
          this.data.buyer_address_country ===
            (billingAddress.data.buyer_address_country ?? billingAddress.data.buyer_address_country_billing)
        );
      },
    };
  }

  private fixAddressForPrivateFields(address, order, asShipping: boolean) {
    const privateCountryPostname = this.appendBillingIfNeeded('buyer_address_private_country', asShipping);
    const countryPostname = this.appendBillingIfNeeded('buyer_address_country', asShipping);
    const privateZipcodePostname = this.appendBillingIfNeeded('buyer_address_private_zipcode', asShipping);
    const zipcodePostname = this.appendBillingIfNeeded('buyer_address_zipcode', asShipping);

    if (
      !order.order.shipping_address.buyer_address_country &&
      order.order.shipping_address.buyer_address_private_country
    ) {
      address[privateCountryPostname] = address[countryPostname];
      delete address[countryPostname];
    }

    if (
      !order.order.shipping_address.buyer_address_zipcode &&
      order.order.shipping_address.buyer_address_private_zipcode
    ) {
      address[privateZipcodePostname] = address[zipcodePostname];
      delete address[zipcodePostname];
    }

    return address;
  }

  private appendBillingIfNeeded(key: string, asShipping = false) {
    return asShipping ? key : key + '_billing';
  }

  private getReturnUrl(): URL {
    const url = new URL(window.location.href);
    const urlSearchParams = new URLSearchParams(url.search);

    urlSearchParams.set('is_express_return', 'true');
    url.search = urlSearchParams.toString();

    return url;
  }

  private async mustUsePrePaymentTaxCalculationDialog() {
    const settings = await firstValueFrom(this.orderformFascade.settings$);

    return [SiteOwners.DIGISTORE24_INC, SiteOwners.DIGISTORE24_LTD].indexOf(settings.config.siteowner_id) >= 0;
  }

  private async mustShowOrderSummary() {
    const settings = await firstValueFrom(this.orderformFascade.settings$);

    return (
      [SiteOwners.DIGISTORE24_GMBH, SiteOwners.DIGISTORE24_MSLW_LIMITED].indexOf(settings.config.siteowner_id) >= 0
    );
  }

  private async waitForOrderUpdateFinished() {
    let hasPendingRequest = await firstValueFrom(this.orderformFascade.pending$);
    while (hasPendingRequest) {
      // when a request was pending,
      await firstValueFrom(
        this.orderformFascade.pending$.pipe(
          filter((pending) => !pending),
          debounceTime(50)
        )
      );
      // check again no request is pending
      hasPendingRequest = await firstValueFrom(this.orderformFascade.pending$);
    }
  }

  private onIntentConfirmed = async (
    intentId: string,
    orderState: Partial<InterfacePost>,
    newAddress: CustomFormData,
    oldAddress: CustomFormData
  ) => {
    this.dialog.closeAll();

    const submitCallback = (submit: boolean) => {
      if (submit) {
        this.orderformFascade.setOrderStore({
          ...orderState,
          payment_provider: this.config,
          payment_provider_meta: {
            valid: true,
            stripe_token: intentId,
          },
        });

        this.submitOrderform(intentId);
      } else {
        this.cancelStripeIntent(intentId);
      }
    };

    if (await this.mustUsePrePaymentTaxCalculationDialog()) {
      if (await this.isCountryOrZipcodeMismatch(newAddress, oldAddress)) {
        this.dialog
          .open(this.dialogUpdatedTaxApproval, {
            data: {
              oldAddress: `${oldAddress.buyer_address_private_country ?? oldAddress.buyer_address_country}, ${
                oldAddress.buyer_address_private_zipcode ?? oldAddress.buyer_address_zipcode
              }`,
              newAddress: `${newAddress.buyer_address_private_country ?? newAddress.buyer_address_country}, ${
                newAddress.buyer_address_private_zipcode ?? newAddress.buyer_address_zipcode
              }`,
            },
          })
          .afterClosed()
          .subscribe(submitCallback);
      } else {
        submitCallback(true);
      }
    } else if (await this.mustShowOrderSummary()) {
      const dialogRef = this.dialog.open(this.dialogOrderSummary, {
        disableClose: false,
      });

      dialogRef.afterClosed().subscribe(submitCallback);

      dialogRef.afterOpened().subscribe(() => {
        document.querySelectorAll('.cdk-overlay-pane').forEach((pane) => {
          pane.classList.add('custom-dialog-class');
        });
        const backdrops = document.querySelectorAll('.cdk-overlay-backdrop');
        if (backdrops.length > 0) {
          backdrops[backdrops.length - 1].remove();
        }
      });
    } else {
      submitCallback(true);
    }
  };

  private async submitOrderform(intentId: string): Promise<void> {
    const buyNowTransaction = this.monitoring.trackUserInteraction('BuyButtonClick', false);
    this._store.dispatch(OrderformActions.formValidation());
    const validationResult = await this.orderformFascade.formValidation.emit();
    if (!validationResult.reduce((valid, result) => valid && result.valid, true)) {
      await this.cancelStripeIntent(intentId);
      this._store.dispatch(OrderformActions.formValidationFailedBeforeSubmit());
      validationResult.forEach((item) => {
        item.invalidFieldList.forEach((field) => {
          field.errors.forEach((err) => {
            this.monitoring.trackValidationError(field.id + ':' + err, 'frontend', item.component);
          });
        });
      });
      await this.scrollToFormError();
      this.orderformFascade.submitFailed.emit();
      buyNowTransaction.end();

      return Promise.resolve();
    } else {
      this._store.dispatch(OrderformActions.formValidationSuccess());
      firstValueFrom(this.orderformFascade.submitOrderForm());
    }
  }

  public async scrollToFormError() {
    await new Promise((resolve) => {
      this._ngZone.runOutsideAngular(() =>
        setTimeout(async () => {
          const errorElements = document.querySelectorAll(
            'orderform-input-errors .error, ds-orderform-buy-button .error'
          );
          const visibleErrors = Array.from(errorElements).filter((element) =>
            isPageBuilderElementVisible(element as HTMLElement)
          );

          /**
           * Try scrolling to each error element until successful
           * Need for elements that exist twice (desktop/mobile) and only one is visible
           *  */
          for (const errorElement of visibleErrors) {
            const beforeScroll = window.scrollY;

            errorElement.scrollIntoView({ block: 'center' });
            this._iframeService.scrollToIframeElementPosition(errorElement.getBoundingClientRect().top);

            // Wait for scroll animation
            await new Promise((resolve) => setTimeout(resolve, 100));

            const afterScroll = window.scrollY;
            const hasScrolled = Math.abs(afterScroll - beforeScroll) > 5;

            if (hasScrolled) {
              break;
            }
          }

          resolve(true);
        }, 100)
      );
    });
  }

  async cancelStripeIntent(intentId: string): Promise<any> {
    const { protocol, hostname, port } = window.location;
    let baseUrl = `${protocol}//${hostname}`;
    if (port) {
      baseUrl += ':' + port;
    }
    const apiUrl = baseUrl + '/buy/stripe_v2/intent/cancel';
    const data = { paymentProviderConfigId: this.config.payment_provider_id, intentId };

    return firstValueFrom(this.http.post(apiUrl, data));
  }

  private trackUserInteraction(order: OrderformOrderState) {
    const labels = this.monitoring.getLabelsFromOrderform(order, true);
    this.monitoring.addLabels(labels);
    this.monitoring.trackUserInteraction('ExpressCheckoutButtonClick', true);
  }

  public async _beforeSubmitFrontendValidation(): Promise<boolean> {
    this._store.dispatch(OrderformActions.formValidation());
    let validationResult = await this.orderformFascade.formValidation.emit();
    validationResult = validationResult.filter((result) => {
      return (
        result.component === 'DS-ORDERFORM-INPUT-CHECKBOXES' &&
        result.invalidFieldList.find((field) => field.id.includes('refund_waiver'))
      );
    });
    if (!validationResult.reduce((valid, result) => valid && result.valid, true)) {
      this._store.dispatch(OrderformActions.formValidationFailedBeforeSubmit());
      await this.scrollToFormError();
      this.orderformFascade.submitFailed.emit();
      return false;
    }

    this._store.dispatch(OrderformActions.formValidationSuccess());

    let hasPendingRequest = await firstValueFrom(this.orderformFascade.pending$);
    while (hasPendingRequest) {
      // when a request was pending,
      await firstValueFrom(
        this.orderformFascade.pending$.pipe(
          filter((pending) => !pending),
          debounceTime(50)
        )
      );
      // check again no request is pending
      hasPendingRequest = await firstValueFrom(this.orderformFascade.pending$);
    }

    return true;
  }

  private async onExpressElementClick(event: StripeExpressCheckoutElementClickEvent) {
    this._store.dispatch(OrderformActions.expressCheckoutButtonClicked());
    this.#tracking.track('BuyButtonClick');
    const order = await firstValueFrom(this.orderformFascade.order$);

    // IMPORTANT: This is a workaround to avoid validation errors when using the express checkout
    // Try to change the payment method to anything except of 'creditcard' to avoid validation errors
    const selectedPaymentProvider = order.payment.find(
      (method) => method.pay_method === 'paypal' || method.pay_method === 'klarna' || method.pay_method === 'venmo'
    );
    if (selectedPaymentProvider) {
      this.orderformFascade.setOrderStore({
        payment_provider: selectedPaymentProvider,
      });
    }

    const settings = order.settings;

    const isFormValid = await this._beforeSubmitFrontendValidation();
    if (!isFormValid) {
      return;
    }

    const isExpressCheckoutInitiable = await firstValueFrom(this.isExpressCheckoutInitiable$);
    const mustUsePrePaymentTaxCalculationDialog = await this.mustUsePrePaymentTaxCalculationDialog();
    const isPaymentPlanSelected = await this.validatePaymentPlanSelection();

    if (!isPaymentPlanSelected) {
      return;
    }
    this.trackUserInteraction(order);
    if (mustUsePrePaymentTaxCalculationDialog && settings.global.is_vat_shown && !isExpressCheckoutInitiable) {
      const dialog = this.dialog.open(this.dialogEnterCountryAndZipCode);
      dialog.afterOpened().subscribe(() => {
        this.expressCheckoutElement.unmount();
        this.expressCheckoutElement.mount('#stripe-express-checkout-dialog-' + this.uniqueInstanceId);
      });
      dialog.afterClosed().subscribe(() => {
        this.expressCheckoutElement.unmount();
        this.expressCheckoutElement.mount('#stripe-express-checkout-' + this.uniqueInstanceId);
      });
      return;
    } else {
      await this.waitForOrderUpdateFinished();

      const phoneNoField = (
        await firstValueFrom(this.orderformFascade.settings$)
      ).contact_form.fields_shipping_address.find((field) => field.postname === 'buyer_address_phone_no');
      const resolveOptions: ClickResolveDetails = {
        emailRequired: true,
        phoneNumberRequired: !!phoneNoField,
        lineItems: await this.getLineItems(),
      };

      const settings = await firstValueFrom(this.orderformFascade.settings$);
      if (settings.contact_form.has_shipping) {
        resolveOptions.shippingAddressRequired = true;
        resolveOptions.shippingRates = [
          {
            id: 'shipping-already-included',
            displayName: $localize`:@@payment.express_checkout.shipping_already_included:Versand bereits inkludiert.`,
            amount: 0,
          },
        ];
      }

      event.resolve(resolveOptions);
    }
  }

  private async getLineItems(): Promise<Array<{ name: string; amount: number }>> {
    const lineItems = [];
    const orderItems = await firstValueFrom(this.orderformFascade.orderItems$);
    const settings = await firstValueFrom(this.orderformFascade.settings$);
    Object.values(orderItems).forEach((item) => {
      const product = settings.product.items.find((product) => product.product_id == item.product_id);
      lineItems.push({
        name: item.quantity.value + 'x ' + product.headline,
        amount: Math.round(this.getValueFromAmountItem(product.total_amounts, settings) * 100),
      });
    });

    const order = await firstValueFrom(this.orderformFascade.order$);

    if (order.order_summary.shipping_total.first.net) {
      lineItems.push({
        name: order.order_summary.shipping_cost_label,
        amount: Math.round(this.getValueFromAmountItem(order.order_summary.shipping_total, settings) * 100),
      });
    }

    if (settings.global.is_vat_shown) {
      order.order_summary.vat_amounts.forEach((vat) => {
        lineItems.push({
          name: vat.label,
          amount: Math.round(vat.vat_1st * 100),
        });
      });
    }

    return lineItems;
  }

  private getValueFromAmountItem(amountItem: OrderformAmountItem, globalSettings: OrderformSettingsState): number {
    if (globalSettings.global.is_vat_shown) {
      return amountItem.first.net;
    } else {
      return amountItem.first.gross;
    }
  }

  private getTotalAmount(order: OrderformOrderState) {
    let amount = order.order_summary.order_total.first.gross;
    if (!amount) {
      amount = order.order_summary.order_total.oth.gross;
    }
    return amount;
  }

  createStripeIntent(order: OrderformOrderState, buyerEmail: string): Promise<any> {
    const { protocol, hostname, port, href } = window.location;
    let baseUrl = `${protocol}//${hostname}`;
    if (port) {
      baseUrl += ':' + port;
    }
    const apiUrl = baseUrl + '/buy/stripe_v2/create_intent';
    const postData = {
      currency: order.order.currency_code.toLowerCase(),
      amount: order.order_summary.order_final.gross_1st,
      email: buyerEmail,
      payment_plan_id: order.order.payment_plan_id,
      payment_provider_id: this.config.payment_provider_id,
      url: href,
    };

    const logData = {
      ...postData,
      frontend_calculated_amount: order.order_summary.order_final.gross_1st,
      order_total_first_gross: order.order_summary.order_total.first.gross,
      order_total_oth_gross: order.order_summary.order_total.oth.gross,
    };

    this.monitoring.logEvent('[Express checkout frontend] create-intent logData', logData);

    return firstValueFrom(this.http.post(apiUrl, postData));
  }

  public async validatePaymentPlanSelection() {
    const orderFormState = await firstValueFrom(this.orderformFascade.order$);
    // Special case for DigiCalls: no payment plan id is set
    if (
      !orderFormState.order.payment_plan_id &&
      (!orderFormState.payment_plans || orderFormState.payment_plans.length === 0)
    ) {
      return true;
    }

    const paymentPlanId = await firstValueFrom(this.orderformFascade.paymentPlanId$);

    if (!paymentPlanId) {
      this._store.dispatch(OrderformActions.formValidationFailedBeforeSubmit());
      this.orderformService.setPaymentPlanError(true);
      return false;
    }

    return true;
  }

  private async isCountryOrZipcodeMismatch(newAddress: CustomFormData, oldAddress: CustomFormData) {
    const fieldsConfig = await firstValueFrom(this.fieldsConfig$);

    const countryFieldConfig = fieldsConfig.find(
      (field) => field.postname === 'buyer_address_private_country' || field.postname === 'buyer_address_country'
    );

    const zipCodeFieldConfig = fieldsConfig.find(
      (field) => field.postname === 'buyer_address_zipcode' || field.postname === 'buyer_address_private_zipcode'
    );

    // Check for country mismatch (across both possible keys)
    const newCountry =
      newAddress[countryFieldConfig?.postname] ||
      newAddress['buyer_address_country'] ||
      newAddress['buyer_address_private_country'];

    const oldCountry =
      oldAddress[countryFieldConfig?.postname] ||
      oldAddress['buyer_address_country'] ||
      oldAddress['buyer_address_private_country'];

    const countryMismatch = newCountry !== oldCountry;

    //Special case for US, if there is no mismatch in country, we don't need to check for zipcode mismatch(except for US)
    if (!countryMismatch && newCountry !== 'US') {
      return false;
    }

    // Check for zipcode mismatch (across both possible keys)
    const newZipCode =
      newAddress[zipCodeFieldConfig?.postname] ||
      newAddress['buyer_address_zipcode'] ||
      newAddress['buyer_address_private_zipcode'];

    const oldZipCode =
      oldAddress[zipCodeFieldConfig?.postname] ||
      oldAddress['buyer_address_zipcode'] ||
      oldAddress['buyer_address_private_zipcode'];

    const zipCodeMismatch = newZipCode !== oldZipCode;

    return countryMismatch || zipCodeMismatch;
  }

  async ngOnDestroy(): Promise<void> {
    if (this.orderFormStateSubscription) {
      this.orderFormStateSubscription.unsubscribe();
    }
    if (this.taxAddressDataSubscription) {
      this.taxAddressDataSubscription.unsubscribe();
    }
  }
}
